import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import type { PatternName, ScoreBreakdown, TallyStep } from '../../engine';
import type { AiSpeed } from '../../app/settings';
import { t } from '../../i18n';
import { itemName, omenName, patternName, statusName } from '../../app/describe';

/** Sparkles around a finished score: left %, top %, delay in ms. */
const SPARKLES: readonly (readonly [number, number, number])[] = [
  [2, 18, 0],
  [88, 8, 90],
  [12, 70, 160],
  [80, 66, 60],
  [46, -4, 220],
  [60, 84, 300],
];

/** One beat of a score being counted: a box (P, M or brass) changes, and says for what. */
export interface ScoreBeat {
  readonly box: 'p' | 'm' | 'brass';
  /** What the beat adds: "+3", "+0.5", "×2", "+¤1". */
  readonly text: string;
  readonly label: string;
  /** P and M once the beat is counted. */
  readonly p: number;
  readonly m: number;
  /** A chance that came up (Long Night). */
  readonly lucky?: boolean;
}

/** A multiplier as shown: whole, or with one decimal (×1.5 in the Long Night). */
export const formatM = (m: number): string =>
  Number.isInteger(m) ? String(m) : (Math.round(m * 10) / 10).toFixed(1);

/**
 * A score as shown (docs/SHOP_DESIGN.md §8.5, LONG_NIGHT_ART_DIRECTION.md §8.3): in full, with
 * thin-space thousands, up to 999 999; then 1.24M and 3.1B; then 4.2e12. Never more than seven
 * characters, so a slammed score's box never changes width.
 */
export function formatScore(n: number): string {
  const v = Math.max(0, Math.floor(n));
  if (v < 1e4) return String(v);
  if (v < 1e6) return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, '\u2009');
  const short = (x: number, unit: string) => {
    const digits = x >= 100 ? 0 : x >= 10 ? 1 : 2;
    return `${Number(x.toFixed(digits))}${unit}`;
  };
  if (v < 999.5e6) return short(v / 1e6, 'M');
  if (v < 999.5e9) return short(v / 1e9, 'B');
  return v.toExponential(1).replace('e+', 'e').replace('.0e', 'e');
}

/** A score whose suffix (M, B, e12) is set smaller, on the same baseline (§8.3). */
export function ScoreNumber({ value }: { value: number }) {
  const text = formatScore(value);
  const m = /^(.*?)([MB]|e\d+)$/.exec(text);
  if (!m) return <>{text}</>;
  return (
    <>
      {m[1]}
      <span className="num-suffix">{m[2]}</span>
    </>
  );
}

/** What a Tally step is named for (Long Night): the item, pattern, Omen or rule. */
function stepLabel(step: TallyStep): string {
  switch (step.source) {
    case 'landing':
      return t('score.step.landing');
    case 'capture':
      return t('score.step.capture');
    case 'crown':
      return t('score.step.crown');
    case 'link':
      return t('score.step.link');
    case 'pattern':
      return patternName((step.id as PatternName | undefined) ?? null);
    case 'fullCircle':
      return t('preview.fullCircle');
    case 'appraisal':
      return itemName(`appraisal.${step.id ?? ''}`);
    case 'status':
      return statusName(step.id ?? '');
    case 'omen':
      return omenName(step.id ?? '');
    case 'houseRule':
      return itemName(`rule.${step.id ?? ''}`);
    default:
      return itemName(step.id ?? '');
  }
}

/**
 * The beats of a chain's score, counted as Balatro counts a hand: each landing, capture and the
 * crown into the points (P), then each link and bonus into the multiplier (M). At the Long Night
 * the Tally gives the beats, step by step: every Superstition, Stamp and Omen in its turn.
 */
export function scoreBeats(b: ScoreBreakdown): ScoreBeat[] {
  if (b.steps && b.steps.length > 0)
    return b.steps
      .filter((s) => !s.pending)
      .map((s) => ({
        box: s.op === 'brass' ? 'brass' : s.op === 'p' ? 'p' : 'm',
        text:
          s.op === 'x'
            ? `×${formatM(s.amount)}`
            : s.op === 'brass'
              ? `+¤${s.amount}`
              : `+${s.op === 'm' ? formatM(s.amount) : s.amount}`,
        label: stepLabel(s),
        p: s.p,
        m: s.m,
        ...(s.chance ? { lucky: true } : {}),
      }));
  const beats: ScoreBeat[] = [];
  let p = 0;
  let m = 0;
  const each = (box: 'p' | 'm', times: number, total: number, label: string) => {
    for (let i = 0; i < times; i++) add(box, total / times, label);
  };
  const add = (box: 'p' | 'm', n: number, label: string) => {
    if (box === 'p') p += n;
    else m += n;
    beats.push({ box, text: `+${n}`, label, p, m });
  };
  each('p', b.points.landings, b.points.landing, t('score.step.landing'));
  each('p', b.points.captures, b.points.capture, t('score.step.capture'));
  if (b.points.crown > 0) add('p', b.points.crown, t('score.step.crown'));
  each('m', b.multiplier.links, b.multiplier.links, t('score.step.link'));
  if (b.multiplier.pattern) add('m', b.multiplier.patternBonus, patternName(b.multiplier.pattern));
  if (b.multiplier.fullCircle) add('m', b.multiplier.fullCircleBonus, t('preview.fullCircle'));
  return beats;
}

/**
 * How long each part of a score takes (ms) at the game's pace (Settings: computer speed): a beat,
 * the score slammed down, the player's score rolling up to it, and a moment to read it all.
 */
export const SCORE_PACE: Record<
  AiSpeed,
  { beat: number; slam: number; roll: number; linger: number }
> = {
  fast: { beat: 110, slam: 280, roll: 450, linger: 300 },
  normal: { beat: 320, slam: 650, roll: 800, linger: 900 },
  slow: { beat: 450, slam: 850, roll: 1000, linger: 1300 },
};
/** However long a chain, its beats take this long at most (ms): a long one counts faster. */
const BEATS_MAX_MS = 3600;
/** The burst coming in, and going away (styles.css). */
const IN_MS = 250;
const OUT_MS = 350;

type Phase = 'count' | 'slam' | 'roll' | 'leaving';

/**
 * A chain's score, counted beat by beat at a pace the eye can follow: P (in a blue box) and M (in
 * a red one) climb, each beat saying what earned it, then P × M is slammed down beneath them, and
 * the player's score beside the board rolls up to it (`onLanded`, MatchScreen). A tap skips to
 * the end. The game waits for it (`onDone`).
 */
export function ScoreBurst({
  breakdown,
  who,
  source,
  pace,
  reduceMotion,
  onTick,
  onLanded,
  onDone,
}: {
  breakdown: ScoreBreakdown;
  who: string;
  source: 'chain' | 'epitaph';
  pace: AiSpeed;
  reduceMotion: boolean;
  onTick?: () => void;
  /** The score is down: the player's score may roll up to it. */
  onLanded: () => void;
  /** All shown: the burst can go. */
  onDone: () => void;
}) {
  const beats = useMemo(() => scoreBeats(breakdown), [breakdown]);
  const [counted, setCounted] = useState(reduceMotion ? beats.length : 0);
  const [phase, setPhase] = useState<Phase>(reduceMotion ? 'roll' : 'count');
  const tick = useEffectEvent(() => onTick?.());
  const landed = useEffectEvent(() => onLanded());
  const done = useEffectEvent(() => onDone());

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const at = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));
    if (reduceMotion) {
      landed();
      at(1800, done);
    } else {
      const p = SCORE_PACE[pace];
      const beat = Math.max(80, Math.min(p.beat, BEATS_MAX_MS / Math.max(1, beats.length)));
      beats.forEach((_, i) =>
        at(IN_MS + i * beat, () => {
          setCounted(i + 1);
          tick();
        }),
      );
      let when = IN_MS + beats.length * beat;
      at(when, () => setPhase('slam'));
      when += p.slam;
      at(when, () => {
        setPhase('roll');
        landed();
      });
      when += p.roll + p.linger;
      at(when, () => setPhase('leaving'));
      at(when + OUT_MS, done);
    }
    return () => timers.forEach(clearTimeout);
  }, [beats, pace, reduceMotion]);

  /** A tap: straight to the end. */
  const skip = () => {
    setCounted(beats.length);
    onLanded();
    onDone();
  };

  const last = counted > 0 ? beats[counted - 1] : undefined;
  const shownP = scoredTotals(phase, breakdown, last).p;
  const shownM = scoredTotals(phase, breakdown, last).m;
  const brass = beats.slice(0, counted).some((b) => b.box === 'brass') ? breakdown.brass : 0;
  const scored = phase !== 'count';
  return (
    <div
      className={`score-burst phase-${phase}`}
      role="status"
      title={t('score.skip')}
      onClick={skip}
    >
      {scored && !reduceMotion && (
        <span className="burst-sparkles" aria-hidden="true">
          {SPARKLES.map(([left, top, delay], i) => (
            <span
              key={i}
              className="fx"
              style={{ left: `${left}%`, top: `${top}%`, animationDelay: `${delay}ms` }}
            />
          ))}
        </span>
      )}
      {/* Who scores, and Skip beside it: kept in its place once the score is in, so nothing moves. */}
      <span className="count-head">
        <span className="burst-who">
          {who} · {source === 'epitaph' ? t('score.epitaph') : t('score.chain')}
        </span>
        <button
          type="button"
          className={`count-skip ${scored ? 'is-done' : ''}`}
          aria-hidden={scored || undefined}
          tabIndex={scored ? -1 : undefined}
          aria-label={t('score.skip')}
          onClick={(e) => {
            e.stopPropagation();
            skip();
          }}
        >
          {t('score.skipButton')}
          <svg
            viewBox="0 0 8 7"
            width="10"
            height="9"
            aria-hidden="true"
            shapeRendering="crispEdges"
          >
            <path
              d="M0 0h1v1h1v1h1v1h1v1h-1v1h-1v1h-1v1h-1zM4 0h1v1h1v1h1v1h1v1h-1v1h-1v1h-1v1h-1z"
              fill="currentColor"
            />
          </svg>
        </button>
      </span>
      <span className="burst-formula">
        <span className={`burst-box burst-p ${last?.box === 'p' && !scored ? 'is-hit' : ''}`}>
          <span key={`p${shownP}`} className="burst-num">
            {formatScore(shownP)}
          </span>
        </span>
        <span className="burst-x">×</span>
        <span className={`burst-box burst-m ${last?.box === 'm' && !scored ? 'is-hit' : ''}`}>
          <span key={`m${shownM}`} className="burst-num">
            {formatM(shownM)}
          </span>
        </span>
      </span>
      {!scored && last && (
        <span
          key={counted}
          className={`burst-beat beat-${last.box} ${last.lucky ? 'is-lucky' : ''}`}
          aria-hidden="true"
        >
          {last.text} {last.label}
          {last.lucky ? ` · ${t('score.lucky')}` : ''}
        </span>
      )}
      {scored && (
        <span className="burst-score">
          <span className="sr-only">= </span>
          {formatScore(breakdown.score)}
        </span>
      )}
      {(brass ?? 0) > 0 && <span className="burst-brass">+¤{brass}</span>}
      {scored && (breakdown.multiplier.pattern || breakdown.multiplier.fullCircle) && (
        <span className="burst-tags">
          {breakdown.multiplier.pattern && <span>{patternName(breakdown.multiplier.pattern)}</span>}
          {breakdown.multiplier.fullCircle && <span>{t('preview.fullCircle')}</span>}
        </span>
      )}
    </div>
  );
}

/** P and M on show: the last beat counted, or the breakdown's totals once the score is down. */
function scoredTotals(
  phase: Phase,
  b: ScoreBreakdown,
  last: ScoreBeat | undefined,
): { p: number; m: number } {
  if (phase !== 'count') return { p: b.points.total, m: b.multiplier.total };
  return { p: last?.p ?? 0, m: last?.m ?? 0 };
}
